import { PaletteColor } from "@brexhq/metal-foundation-shared";
import styled from "@emotion/styled";
import { border, padding } from "polished";
import React from "react";

import { getModuleThemeColors } from "@brexhq/next/src/components/pageBuilder/ModuleLayout";
import { Text } from "@brexhq/shared/components/Typography/Text";
import { ModuleThemeType } from "@brexhq/shared/types/ModuleTheme";
import { ComparisonTableCell as ComparisonTableCellType } from "@brexhq/shared/types/modules/BlockRowsModule/BlockRowItems/ComparisonTable";
import { blocksToText } from "@brexhq/shared/utils/sanity/blocksToText";

import { IconPicker } from "../../IconPicker/IconPicker";

export const Cell = styled.td<{
  isRowHeaderCell?: boolean;
  columnCount?: number;
}>(({ isRowHeaderCell, columnCount }) => ({
  padding: 0,
  margin: 0,
  height: "88px",
  // Apply consistent width for row header cells (first column)
  ...(isRowHeaderCell && columnCount && columnCount > 1
    ? { width: "25%" }
    : {}),
}));

export const CellContent = styled.div<{
  isMainHeaderCell?: boolean;
  isRowHeaderCell?: boolean;
  borders?: PaletteColor;
}>(({ isMainHeaderCell, isRowHeaderCell, borders, theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: isMainHeaderCell || isRowHeaderCell ? "flex-start" : "center",
  textAlign: isMainHeaderCell || isRowHeaderCell ? "left" : "center",
  ...padding(
    theme.spacing[24],
    theme.spacing[20],
    ...(isMainHeaderCell ? [theme.spacing[24], 0] : []),
  ),
  ...(borders ? border("bottom", 1, "solid", theme.colors.border.default) : []),
  [theme.breakpoints.SML]: {
    ...padding(theme.spacing[24], theme.spacing[8]),
  },
}));

const IconContainer = styled.div({
  position: "relative",
});

const IconSuperScript = styled.span<{ color: PaletteColor }>(({ color }) => ({
  color,
  position: "absolute",
  top: 0,
  right: "-8px",
}));

interface Props {
  cell: ComparisonTableCellType;
  theme: ModuleThemeType;
}

export const ComparisonTableCell = ({
  cell: { cellText, cellTextRaw, icon, iconColor, color, iconSuperscript },
  theme,
}: Props) => {
  const {
    standard: { textSecondary },
  } = getModuleThemeColors(theme);

  const resolvedIconColor = color ?? iconColor;
  const resolvedCellText = cellText ?? cellTextRaw;

  return (
    <Cell>
      <CellContent>
        {/* In practice, cell should only ever have either icon OR text OR neither */}
        {icon && (
          <IconContainer>
            <IconPicker
              theme={theme}
              marginTop="none"
              size={{ LRG: "med", MED: "med", SML: "sml" }}
              icon={icon}
              color={resolvedIconColor ?? "single"}
            />
            {iconSuperscript && (
              <IconSuperScript color={textSecondary}>
                {iconSuperscript}
              </IconSuperScript>
            )}
          </IconContainer>
        )}
        {resolvedCellText && (
          <Text
            as="p"
            color={textSecondary}
            components={{
              LRG: "Body2",
              MED: "Body2",
              SML: "Legal",
            }}
          >
            {blocksToText(resolvedCellText)}
          </Text>
        )}
      </CellContent>
    </Cell>
  );
};
